import type { FC, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import translate from "src/lib/translate";
import { TourGuideAnchor } from "@9amhealth/shared";
import { ChatTourAnchor } from "./chatTourConstants";
import { ChatHeaderBar } from "./leapChat.styles";
import ConversationListCubit from "./state/ConversationListCubit";
import { CHAT_NEW_PATH } from "./leapChatPaths";

export const HeaderIconButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background: none;
  border: none;
  padding: 0.8rem;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

const UnreadBadgeDot = styled.span`
  position: absolute;
  top: 0.45rem;
  right: 0.35rem;
  width: 0.6rem;
  aspect-ratio: 1;
  border-radius: 50%;
  background: var(--color-highlight-blue);
  pointer-events: none;
`;

const ArchiveIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      stroke="#212121"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeOpacity=".8"
      strokeWidth="2"
      d="M3 12h18M3 6h18M3 18h12"
    />
  </svg>
);

export const NewConversationIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      stroke="#212121"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeOpacity=".8"
      strokeWidth="2"
      d="M11 4H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 6.28 2 7.12 2 8.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 22 5.12 22 6.8 22h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 19.72 20 18.88 20 17.2V13M8 16h1.675c.489 0 .733 0 .963-.055.204-.05.4-.13.579-.24.201-.123.374-.296.72-.642L21.5 5.5a2.121 2.121 0 0 0-3-3l-9.563 9.563c-.346.346-.519.519-.642.72a2 2 0 0 0-.24.579c-.055.23-.055.474-.055.963z"
    />
  </svg>
);

const ChatDetailHeader: FC<{
  showArchive?: boolean;
  showNew?: boolean;
  rightSlot?: ReactNode;
}> = ({ showArchive = true, showNew = false, rightSlot }) => {
  const navigate = useNavigate();
  const [{ conversations }, cubit] = useBlocNext(ConversationListCubit);

  const hasUnread = conversations.some((c) => c.unreadMessageCount > 0);

  return (
    <ChatHeaderBar>
      {showArchive ? (
        <TourGuideAnchor name={ChatTourAnchor.DRAWER_TOGGLE}>
          <HeaderIconButton
            onClick={() => cubit.toggleDrawer()}
            aria-label={translate("leapChat.aria.viewConversations")}
          >
            <ArchiveIcon />
            {hasUnread && <UnreadBadgeDot />}
          </HeaderIconButton>
        </TourGuideAnchor>
      ) : (
        <span />
      )}
      {rightSlot ??
        (showNew ? (
          <TourGuideAnchor name={ChatTourAnchor.NEW_CONVERSATION}>
            <HeaderIconButton
              onClick={() =>
                navigate(CHAT_NEW_PATH, { state: { autoFocus: true } })
              }
              aria-label={translate("leapChat.aria.newConversation")}
            >
              <NewConversationIcon />
            </HeaderIconButton>
          </TourGuideAnchor>
        ) : (
          <span />
        ))}
    </ChatHeaderBar>
  );
};

export default ChatDetailHeader;
